/**
 * visibility logic for cubes (svg) in the puzzle map that reveals
 * more cubes as progress % increases.
 *
 * Current visibility stages:
 * - 0%:   No cubes visible
 * - 20%:  Reveals cubes in the right half of the bottom third
 * - 40%:  Reveals complex pattern in bottom third
 * - 60%:  Adds middle third pattern
 * - 80%:  Expands middle third pattern
 * - 100%: adds more cubes in the top third and completes the puzzle
 *
 * grid is divided into 3 equal sections vertically with each section having
 * a pattern function that determines cube visibility based on row and column positions of the cube.
 *
 */

interface GridConfig {
  columnsPerRow: number;
  numberOfRows: number;
}

interface SectionConfig {
  startRow: number;
  endRow: number;
}

// type -> [[minPerc, maxPerc], normalizedPerc] % values
const PROGRESS_STAGE_RANGES: Array<[[number, number], number]> = [
  [[0, 14], 0],
  [[15, 34], 20],
  [[35, 54], 40],
  [[55, 74], 60],
  [[75, 99], 80],
  [[100, 100], 100],
];

const calculateThirdBoundaries = (totalRows: number) => ({
  topThird: { startRow: 0, endRow: Math.floor(totalRows * (1 / 3)) },
  middleThird: {
    startRow: Math.floor(totalRows * (1 / 3)),
    endRow: Math.floor(totalRows * (2 / 3)),
  },
  bottomThird: {
    startRow: Math.floor(totalRows * (2 / 3)),
    endRow: totalRows,
  },
});

const isInSection = (row: number, section: SectionConfig) => {
  return row >= section.startRow && row < section.endRow;
};

const calculateColumnThreshold = (columnsPerRow: number, fraction: number) => {
  return Math.floor(columnsPerRow * fraction);
};

const bottomThirdPattern = (row: number, col: number, gridDimensions: GridConfig, section: SectionConfig) => {
  const { columnsPerRow } = gridDimensions;
  const sectionHeight = Math.floor((section.endRow - section.startRow) / 3);

  if (row >= section.startRow + 2 * sectionHeight) return true;

  // Middle section: display 1/4th of the row from the left and 5/8th of the row from the right
  if (row >= section.startRow + sectionHeight) {
    const leftQuarter = calculateColumnThreshold(columnsPerRow, 1 / 4);
    const rightFiveEighths = calculateColumnThreshold(columnsPerRow, 5 / 8);
    return col < leftQuarter || col >= columnsPerRow - rightFiveEighths;
  }

  const halfSectionHeight = Math.floor(sectionHeight / 2);
  if (row >= section.startRow + halfSectionHeight) {
    return col >= calculateColumnThreshold(columnsPerRow, 5 / 8);
  }
  return col >= calculateColumnThreshold(columnsPerRow, 1 / 2);
};

const middleThirdPattern = (
  row: number,
  col: number,
  gridDimensions: GridConfig,
  section: SectionConfig,
  progress: number,
) => {
  const { columnsPerRow } = gridDimensions;
  const sectionHeight = Math.floor(section.endRow - section.startRow);

  if (progress === 60) {
    const halfHeight = Math.floor(sectionHeight / 2);
    if (row >= section.startRow + halfHeight) {
      return col >= calculateColumnThreshold(columnsPerRow, 5 / 8);
    }
    return col >= calculateColumnThreshold(columnsPerRow, 1 / 2);
  }

  if (progress >= 80) {
    const thirdHeight = Math.floor(sectionHeight / 3);
    if (row >= section.startRow + 2 * thirdHeight) {
      return col >= calculateColumnThreshold(columnsPerRow, 3 / 8);
    }

    if (row >= section.startRow + thirdHeight) {
      const halfThirdHeight = Math.floor(thirdHeight / 2);
      if (row >= section.startRow + thirdHeight + halfThirdHeight) {
        return col >= calculateColumnThreshold(columnsPerRow, 3 / 8);
      }

      const gapStart = calculateColumnThreshold(columnsPerRow, 1 / 4);
      const gapWidth = calculateColumnThreshold(columnsPerRow, 1 / 8);
      const rightFill = calculateColumnThreshold(columnsPerRow, 3 / 8);
      return col >= rightFill && !(col >= gapStart && col < gapStart + gapWidth);
    }

    return col >= calculateColumnThreshold(columnsPerRow, 1 / 2);
  }

  return false;
};

const topThirdPattern = (row: number, col: number, gridDimensions: GridConfig, section: SectionConfig) => {
  const { columnsPerRow } = gridDimensions;
  const sectionHeight = Math.floor(section.endRow / 3);

  if (row >= 2 * sectionHeight) {
    return col >= calculateColumnThreshold(columnsPerRow, 1 / 2);
  }

  if (row >= sectionHeight) {
    const gapWidth = columnsPerRow > 16 ? 2 : 1;
    const rightHalf = calculateColumnThreshold(columnsPerRow, 1 / 2);
    const gapStart = Math.floor(rightHalf + rightHalf / 3);
    return col >= rightHalf && !(col >= gapStart && col < gapStart + gapWidth);
  }

  return col >= calculateColumnThreshold(columnsPerRow, 3 / 4);
};

const getNormalizedProgress = (progress: number): number => {
  const stage = PROGRESS_STAGE_RANGES.find(([[min, max]]) => progress >= min && progress <= max);
  return stage ? stage[1] : 0;
};

export const determineCubeVisibility = (
  position: { row: number; col: number },
  gridDimensions: GridConfig,
  progress: number,
  enableAutoAnimation?: boolean,
) => {
  const { row, col } = position;
  const { columnsPerRow, numberOfRows } = gridDimensions;
  const { topThird, middleThird, bottomThird } = calculateThirdBoundaries(numberOfRows);
  const stage = getNormalizedProgress(progress);

  // if autoplay is enabled, show all the cubes that are shown if the progress was 100%
  if (enableAutoAnimation) {
    const sections = [
      { section: bottomThird, pattern: bottomThirdPattern },
      {
        section: middleThird,
        pattern: (r: number, c: number, g: GridConfig, s: SectionConfig) => middleThirdPattern(r, c, g, s, 100),
      },
      { section: topThird, pattern: topThirdPattern },
    ];

    const matchingSection = sections.find(({ section }) => isInSection(row, section));
    if (matchingSection) {
      return matchingSection.pattern(row, col, gridDimensions, matchingSection.section);
    }
  }

  if (stage === 0) return false;

  if (stage === 20) {
    const bottomThird = calculateThirdBoundaries(numberOfRows).bottomThird;
    return row >= bottomThird.startRow && col > calculateColumnThreshold(columnsPerRow, 1 / 2);
  }

  if (isInSection(row, bottomThird)) {
    return bottomThirdPattern(row, col, gridDimensions, bottomThird);
  }

  if (stage >= 60 && isInSection(row, middleThird)) {
    return middleThirdPattern(row, col, gridDimensions, middleThird, stage);
  }

  if (stage === 100 && isInSection(row, topThird)) {
    return topThirdPattern(row, col, gridDimensions, topThird);
  }

  return false;
};
